import { BaseDisplaySet } from './BaseDisplaySet.js';
import { ImageStackDisplaySet } from './ImageStackDisplaySet.js';
import { isUnsafeKey } from '../safeFunctions/schema.js';
import { isReconstructableInstances } from './isReconstructableInstances.js';
const RESERVED_ATTRIBUTE_KEYS = new Set([
    'imageIds',
    'underlyingImageIds',
    'instances',
    'displaySetId',
    'splitKey',
    'splitRuleId',
    'splitGroupId',
    'handler',
    'isDisplayable',
]);
function isAssignable(target, key) {
    let obj = target;
    while (obj) {
        const descriptor = Object.getOwnPropertyDescriptor(obj, key);
        if (descriptor) {
            if (descriptor.get || descriptor.set) {
                return typeof descriptor.set === 'function';
            }
            return descriptor.writable !== false;
        }
        obj = Object.getPrototypeOf(obj);
    }
    return true;
}
function runCustomAttributes(group, options) {
    const { instances, matchedRule } = group;
    const first = instances[0];
    if (!matchedRule.customAttributes || !first) {
        return undefined;
    }
    const sopClassUids = [
        ...new Set(instances.map((i) => i.SOPClassUID).filter(Boolean)),
    ];
    const isMultiFrame = Number(first.NumberOfFrames) > 1;
    return (matchedRule.customAttributes({ instance: first, isMultiFrame, sopClassUids }, {
        instances,
        splitNumber: options.splitNumber,
        descriptionName: options.descriptionName,
        series: group.series,
    }) ?? undefined);
}
function applyCustomAttributes(displaySet, attributes) {
    if (!attributes) {
        return;
    }
    for (const [key, value] of Object.entries(attributes)) {
        if (RESERVED_ATTRIBUTE_KEYS.has(key) || isUnsafeKey(key)) {
            continue;
        }
        if (isAssignable(displaySet, key)) {
            displaySet[key] = value;
        }
    }
}
export function createDisplaySetFromGroup(group, options = {}) {
    const attributes = runCustomAttributes(group, options);
    const { instances } = group;
    const baseDisplaySetId = instances[0]?.SeriesInstanceUID ??
        `display-set-${instances[0]?.imageId ?? 'unknown'}`;
    const displaySetId = options.displaySetId ??
        (options.splitNumber
            ? `${baseDisplaySetId}:${options.splitNumber}`
            : baseDisplaySetId);
    const first = instances[0];
    const { handler } = group.matchedRule;
    const underlyingImageIds = instances
        .map((i) => i.imageId)
        .filter(Boolean);
    let displaySet;
    if (handler === 'unsupported') {
        displaySet = new BaseDisplaySet({
            displaySetId,
            instances,
            imageIds: [],
            underlyingImageIds,
            isDisplayable: false,
        });
    }
    else if (handler !== undefined && handler !== 'image') {
        displaySet = new BaseDisplaySet({
            displaySetId,
            instances,
            imageIds: options.imageIds ?? underlyingImageIds,
            underlyingImageIds,
        });
    }
    else {
        displaySet = ImageStackDisplaySet.fromInstances(instances, {
            displaySetId,
            imageIds: options.imageIds,
        });
    }
    displaySet.splitKey = group.splitKey;
    displaySet.splitRuleId = group.matchedRule.id;
    displaySet.splitGroupId = group.matchedRule.groupId ?? group.matchedRule.id;
    if (handler !== undefined) {
        displaySet.handler = handler;
    }
    if (group.matchedRule.viewportTypes !== undefined) {
        displaySet.viewportTypes = [...group.matchedRule.viewportTypes];
    }
    displaySet.sopClassUids = [
        ...new Set(instances.map((i) => i.SOPClassUID).filter(Boolean)),
    ];
    if (options.splitNumber !== undefined) {
        displaySet.splitNumber = options.splitNumber;
    }
    displaySet.isMultiFrame ??= Number(first?.NumberOfFrames) > 1;
    applyCustomAttributes(displaySet, attributes);
    if ((handler === undefined || handler === 'image') &&
        typeof displaySet.isReconstructable !== 'boolean') {
        displaySet.isReconstructable =
            !displaySet.isClip && isReconstructableInstances(instances);
    }
    return displaySet;
}
